import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { getSlotAvailability } from "../../services/observationService";
import { createTimetableEntry } from "../../services/timetableService";
import { PERIOD_CHOICES } from "../../utils/constants/observations";
import { runErrorMessage } from "../timetable/runErrors";
import useAuth from "../../utils/hooks/useAuth";

/** The visit fields a picked `lesson` fills in: its class group when the
 *  timetable names one, and always its subject — null for a lesson with none,
 *  so a visit moved onto it does not keep the old lesson's subject. The open
 *  grid (no lesson) fills in nothing. */
export const lessonFields = (lesson) =>
  lesson
    ? {
        ...(lesson.group?.uuid ? { group: lesson.group.uuid } : {}),
        domain_subject: lesson.subject?.uuid ?? null,
      }
    : {};

/**
 * Availability-aware period picker.
 *
 * It used to know one thing — does this teacher have a lesson then — and paint
 * two states from it. Everything else it got wrong silently: a slot already
 * booked looked free until the POST came back in English, and the OBSERVER was
 * never considered at all, which is how 4 of 29 upcoming visits ended up
 * scheduled for a period their observer spends teaching their own class.
 *
 * Now one request answers all three (`/observations/slots/`) and the tile says
 * which of them is in the way. Colour is never the only signal — every
 * unavailable tile carries the reason in words.
 *
 * onChange(period, lesson|null) — `lesson` carries group + subject + start_time,
 * or null on the open grid a teacher with no published timetable falls back to.
 */
export default function PeriodPicker({
  teacherUuid,
  date,
  value,
  onChange,
  disabled,
  observerUuid,
  observationUuid,
}) {
  const { t } = useTranslation();
  const { user } = useAuth();
  // No `observerUuid` means the server defaults the observer to the caller, so
  // "the observer is teaching" is "YOU are teaching" — which is what a teacher
  // booking a colleague needs to read.
  const observerIsSelf = !observerUuid || observerUuid === user?.uuid;
  const [slots, setSlots] = useState(null);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(null); // period being filled in
  const [groupUuid, setGroupUuid] = useState("");
  const [saving, setSaving] = useState(false);
  const [addError, setAddError] = useState("");

  const load = useCallback(
    (signal) => {
      if (!teacherUuid || !date) return Promise.resolve();
      setLoading(true);
      return getSlotAvailability({
        params: {
          teacher: teacherUuid,
          date,
          observer: observerUuid || undefined,
          observation: observationUuid || undefined,
        },
        signal,
      })
        .then((data) => {
          setSlots(data);
          return data;
        })
        .catch((err) => {
          if (err?.code === "ERR_CANCELED") return null;
          setSlots(null);
          return null;
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [teacherUuid, date, observerUuid, observationUuid],
  );

  useEffect(() => {
    const controller = new AbortController();
    setAdding(null);
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const byPeriod = useMemo(() => {
    const map = new Map();
    (slots?.periods || []).forEach((row) => map.set(row.period, row));
    return map;
  }, [slots]);

  const saveLesson = async (period) => {
    if (!groupUuid) {
      setAddError(t("schedule.classRequired"));
      return;
    }
    setSaving(true);
    setAddError("");
    try {
      await createTimetableEntry({
        body: {
          teacher: teacherUuid,
          group: groupUuid,
          weekday: slots.weekday,
          period,
          start_time: byPeriod.get(period)?.bell_start || "08:00",
        },
      });
      setAdding(null);
      setGroupUuid("");
      // Selected off the RELOADED payload, not off `null`: the caller reads the
      // class out of the lesson it is handed, and passing null booked the visit
      // with no group on the very slot that was just given one.
      const fresh = await load();
      const lesson = (fresh?.periods || []).find((r) => r.period === period)?.lesson;
      // Picking it is the point of adding it — anything else makes the manager
      // click the tile they just created.
      onChange(period, lesson || null);
    } catch (err) {
      // The timetable write path answers with {code, detail}; `code` is what
      // says "this class is already in a lesson then" in the reader's own
      // language. Indexing `data.period[0]` by hand printed "T" — the first
      // CHARACTER of an English sentence, because that field is a bare string
      // here and not the list DRF usually wraps.
      setAddError(runErrorMessage(err, t, t("schedule.lessonSaveFailed")));
    } finally {
      setSaving(false);
    }
  };

  if (!date) {
    return (
      <p className="text-xs font-bold text-slate-400 px-1">{t("upcoming.pickDateFirst")}</p>
    );
  }

  if (loading && !slots) {
    return (
      <div className="grid grid-cols-2 gap-2" aria-busy="true">
        {PERIOD_CHOICES.map((p) => (
          <div key={p} className="h-[62px] animate-pulse bg-slate-100 rounded-xl" />
        ))}
      </div>
    );
  }

  // The endpoint failed (or has not run yet): an open grid keeps the form
  // usable rather than trapping the booking behind a read.
  if (!slots) {
    return <OpenGrid value={value} disabled={disabled} onChange={onChange} />;
  }

  const canAdd = Boolean(slots.can_add_lesson) && !disabled;
  const anyLesson = (slots.periods || []).some((row) => row.lesson);

  return (
    <div className="space-y-2">
      {!slots.teacher_has_timetable ? (
        <Notice tone="amber">
          {canAdd
            ? t("upcoming.teacherNoTimetableManager")
            : t("upcoming.teacherNoTimetable")}
        </Notice>
      ) : (
        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 px-1">
          {t("upcoming.periodPickerHint")}
        </p>
      )}

      {slots.teacher_has_timetable && !anyLesson && (
        <Notice tone="indigo">
          {canAdd
            ? t("upcoming.noLessonsAddOrPickAnother")
            : t("upcoming.noLessonsPickAnotherDate")}
        </Notice>
      )}

      {/* No timetable at all and nobody here can write one: the old open grid,
          so a sparse-data peer stays bookable. */}
      {!slots.teacher_has_timetable && !canAdd ? (
        <OpenGrid value={value} disabled={disabled} onChange={onChange} />
      ) : (
        <div className="grid grid-cols-2 gap-2">
          {PERIOD_CHOICES.map((period) => {
            const row = byPeriod.get(period) || { period };
            if (adding === period) {
              return (
                <AddLessonTile
                  key={period}
                  period={period}
                  bell={row.bell_start}
                  classes={slots.classes || []}
                  groupUuid={groupUuid}
                  onGroupChange={(uuid) => {
                    setGroupUuid(uuid);
                    setAddError("");
                  }}
                  saving={saving}
                  error={addError}
                  onSave={() => saveLesson(period)}
                  onCancel={() => {
                    setAdding(null);
                    setAddError("");
                  }}
                />
              );
            }
            return (
              <SlotTile
                key={period}
                row={row}
                observerIsSelf={observerIsSelf}
                selected={value === period}
                disabled={disabled}
                canAdd={canAdd}
                onSelect={() => onChange(period, row.lesson)}
                onAdd={() => {
                  setAdding(period);
                  setGroupUuid("");
                  setAddError("");
                }}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

const TILE =
  "p-3 rounded-xl border text-left transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2";

function SlotTile({ row, selected, disabled, canAdd, observerIsSelf, onSelect, onAdd }) {
  const { t } = useTranslation();
  const { period, lesson, booked, observer_conflict: conflict } = row;

  // A visit the observer is already attending is the one hard stop the API also
  // enforces. Their own lesson is a warning: managers legitimately book over one
  // (the lesson is moving, a colleague is covering), and only 27 of 202 accounts
  // publish a timetable at all, so it is not authority enough to refuse on.
  const blocked = Boolean(booked) || conflict === "visit";
  const warned = !blocked && conflict === "lesson";

  const reason = booked
    ? booked.observer
      ? t("upcoming.slotTakenBy", { name: booked.observer })
      : t("upcoming.slotTaken")
    : conflict === "visit"
      ? t(observerIsSelf ? "upcoming.slotYouAtVisit" : "upcoming.slotObserverAtVisit")
      : conflict === "lesson"
        ? t(observerIsSelf ? "upcoming.slotYouTeaching" : "upcoming.slotObserverTeaching", {
            group: row.observer_lesson_group || "",
          })
        : null;

  if (!lesson && !blocked) {
    if (!canAdd) {
      return (
        <div
          className={`${TILE} bg-slate-50 border-dashed border-slate-200 text-slate-400`}
        >
          <PeriodLine period={period} muted />
          <p className="text-[10px] font-bold mt-0.5">
            {reason || t("upcoming.slotNoLesson")}
          </p>
        </div>
      );
    }
    // The offer stays quiet until it is wanted. Painting "+ ДОБАВИТЬ УРОК" on
    // every free period drowns the one tile that matters — the lesson the
    // teacher actually teaches — under six identical calls to action.
    return (
      <button
        type="button"
        onClick={onAdd}
        className={`${TILE} group bg-slate-50 border-dashed border-slate-200 text-slate-400 hover:border-indigo-300 hover:bg-white hover:text-indigo-600 focus-visible:border-indigo-300`}
      >
        <PeriodLine period={period} time={row.bell_start} muted />
        <p className="text-[10px] font-bold mt-0.5 group-hover:hidden group-focus-visible:hidden">
          {/* An observer who cannot come is worth more than "no lesson" — it is
              the fact that survives adding one. */}
          {reason || t("upcoming.slotNoLesson")}
        </p>
        <p className="hidden text-[10px] font-black uppercase tracking-wide mt-0.5 group-hover:block group-focus-visible:block">
          + {t("schedule.addLesson")}
        </p>
      </button>
    );
  }

  const tone = selected
    ? "bg-indigo-600 border-indigo-600 text-white shadow-md shadow-indigo-100"
    : blocked
      ? "bg-slate-50 border-slate-200 text-slate-400 cursor-not-allowed"
      : warned
        ? "bg-amber-50/60 border-amber-200 text-slate-700 hover:border-amber-400"
        : "bg-white border-slate-200 text-slate-700 hover:border-indigo-300 hover:bg-indigo-50";

  return (
    <button
      type="button"
      disabled={blocked || disabled || !lesson}
      onClick={onSelect}
      className={`${TILE} ${tone}`}
    >
      <PeriodLine
        period={period}
        time={lesson?.start_time || row.bell_start}
        selected={selected}
        muted={blocked}
      />
      {lesson?.group?.name && (
        <p
          className={`text-[10px] font-bold truncate mt-0.5 ${
            selected ? "text-white" : blocked ? "text-slate-400" : "text-slate-500"
          }`}
        >
          {lesson.group.name}
        </p>
      )}
      {reason && (
        <p
          className={`text-[10px] font-bold truncate mt-0.5 ${
            selected ? "text-white/90" : warned ? "text-amber-700" : "text-slate-400"
          }`}
        >
          {reason}
        </p>
      )}
    </button>
  );
}

function PeriodLine({ period, time, selected, muted }) {
  return (
    <div className="flex items-center justify-between gap-1">
      <span className="text-[11px] font-black uppercase tracking-widest">P{period}</span>
      {time && (
        <span
          className={`text-[10px] font-bold ${
            selected ? "text-white/90" : muted ? "text-slate-300" : "text-slate-400"
          }`}
        >
          {time}
        </span>
      )}
    </div>
  );
}

function AddLessonTile({
  period,
  bell,
  classes,
  groupUuid,
  onGroupChange,
  saving,
  error,
  onSave,
  onCancel,
}) {
  const { t } = useTranslation();
  return (
    <div className="col-span-2 rounded-xl border border-indigo-200 bg-indigo-50/50 p-3 space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-black uppercase tracking-widest text-indigo-700">
          {t("schedule.addLesson")} · P{period}
          {bell ? ` · ${bell}` : ""}
        </span>
        <button
          type="button"
          onClick={onCancel}
          className="text-[9px] font-black text-slate-500 uppercase tracking-widest hover:text-slate-700"
        >
          {t("common.cancel")}
        </button>
      </div>
      <div className="flex gap-2">
        {/* eslint-disable-next-line jsx-a11y/no-autofocus -- the tile replaced
            the control the user just clicked; landing focus anywhere else means
            a keyboard user has to hunt for what opened. */}
        <select
          autoFocus
          value={groupUuid}
          onChange={(e) => onGroupChange(e.target.value)}
          aria-label={t("schedule.lessonGroupLabel")}
          className="flex-1 min-w-0 px-3 py-2 bg-white border border-slate-200 rounded-lg text-xs font-bold text-slate-900 outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500"
        >
          <option value="">{t("schedule.pickClass")}</option>
          {classes.map((g) => {
            const busy = (g.busy_periods || []).includes(period);
            return (
              <option key={g.uuid} value={g.uuid} disabled={busy}>
                {busy ? t("schedule.classBusyOption", { name: g.name }) : g.name}
              </option>
            );
          })}
        </select>
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-indigo-700 disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
        >
          {saving ? t("schedule.checking") : t("schedule.saveLesson")}
        </button>
      </div>
      {error && (
        <p role="alert" className="text-[10px] font-black text-rose-600">
          {error}
        </p>
      )}
    </div>
  );
}

function Notice({ tone, children }) {
  const skin =
    tone === "amber"
      ? "text-amber-700 bg-amber-50 border-amber-100"
      : "text-indigo-700 bg-indigo-50/60 border-indigo-100";
  return (
    <p className={`text-[10px] font-bold rounded-lg border px-3 py-2 ${skin}`}>{children}</p>
  );
}

function OpenGrid({ value, disabled, onChange }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
      {PERIOD_CHOICES.map((p) => (
        <button
          key={p}
          type="button"
          disabled={disabled}
          onClick={() => onChange(p, null)}
          className={`p-3.5 rounded-xl border text-center transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 ${
            value === p
              ? "bg-indigo-600 border-indigo-600 text-white shadow-md shadow-indigo-100"
              : "bg-slate-50 border-slate-200 text-slate-600 hover:border-indigo-200"
          }`}
        >
          <span className="text-xs font-black uppercase tracking-widest">P{p}</span>
        </button>
      ))}
    </div>
  );
}
